/**
 * g1xl-v2#1053 Part 3 — the light index the board picks its lines from.
 *
 * The owning edge's REST `/api/v1/markets` speaks the WS filter vocabulary
 * (`?sport=`, `?class=`, `?game=` — comma-separated allow-lists, include
 * semantics; HYDRA_API "REST — same vocabulary") behind the same
 * Origin-allowlist / `?token=` policy as the consumer socket, with CORS for
 * the g1 origins. Two steps keep it light against a 230-game CFB week
 * (he2 2026-10-02: the three board classes together are 12.4 MB / 12,933
 * rows; the moneyline class alone 612 KB / 667 rows):
 *
 *   1. `?sport=CFB&class=moneyline` — every game's moneyline rows, which
 *      name the games and date them;
 *   2. `?sport=CFB&class=spread,total&game=k1,k2,…` — the ladders of the
 *      games inside the board's day window only, batched so no URL grows
 *      past what a proxy will carry.
 *
 * The REST base is the feed's own consumer URL with the scheme swapped
 * (`wss://he2.qeezi.com/ws?token=…` → `https://he2.qeezi.com`), so a core's
 * bearer rides along as `?token=` exactly as it does on the socket.
 */
import type { HydraMarket } from '@/clients/HydraClient';
import { releaseBody } from '@/lib/infra/responseBody';

export interface EngineRest {
  /** `https://host[:port]` — no path. */
  base: string;
  token: string | null;
}

/** The REST origin of a feed from its consumer WS URL; null when the URL is
 *  not something a browser can turn into an HTTP origin. */
export function engineRestFromWsUrl(wsUrl: string | null | undefined): EngineRest | null {
  if (!wsUrl) return null;
  let u: URL;
  try { u = new URL(wsUrl); } catch { return null; }
  const proto = u.protocol === 'wss:' ? 'https:'
    : u.protocol === 'ws:' ? 'http:'
    : u.protocol === 'https:' || u.protocol === 'http:' ? u.protocol
    : null;
  if (!proto) return null;
  return { base: `${proto}//${u.host}`, token: u.searchParams.get('token') };
}

export interface MarketsIndexQuery {
  /** Uppercase wire sport code (`CFB`). */
  sport: string;
  classes: readonly string[];
  /** Exact canonical keys (either-key membership on the engine). */
  games?: readonly string[];
}

export interface MarketsIndexOptions {
  signal?: AbortSignal;
  fetchImpl?: typeof fetch;
  /** Keys per request on the `?game=` axis (URL length bound). */
  gamesPerRequest?: number;
}

/** One `?game=` batch — exported for the URL-shape test. */
export function marketsIndexUrl(rest: EngineRest, q: MarketsIndexQuery, gamesBatch?: readonly string[]): string {
  const params = new URLSearchParams();
  params.set('sport', q.sport);
  if (q.classes.length > 0) params.set('class', q.classes.join(','));
  if (gamesBatch && gamesBatch.length > 0) params.set('game', gamesBatch.join(','));
  if (rest.token) params.set('token', rest.token);
  return `${rest.base}/api/v1/markets?${params.toString()}`;
}

export const DEFAULT_GAMES_PER_REQUEST = 40;

async function fetchOne(url: string, opts: MarketsIndexOptions): Promise<HydraMarket[]> {
  const doFetch = opts.fetchImpl ?? fetch;
  // A plain GET with no custom headers is a CORS "simple request": no
  // preflight, the browser stamps `Origin`, the engine's allowlist answers.
  const res = await doFetch(url, { signal: opts.signal, credentials: 'omit' });
  if (!res.ok) { releaseBody(res); throw new Error(`markets index ${res.status}`); }
  const body = (await res.json()) as { markets?: unknown };
  if (!body || !Array.isArray(body.markets)) throw new Error('markets index: malformed body');
  return body.markets as HydraMarket[];
}

/** The index rows for `q`, across as many `?game=` batches as it takes.
 *  Throws on any non-2xx / malformed answer — the caller falls back to the
 *  sport-wide board rather than drawing from a partial index. */
export async function fetchMarketsIndex(rest: EngineRest, q: MarketsIndexQuery, opts: MarketsIndexOptions = {}): Promise<HydraMarket[]> {
  if (!q.games || q.games.length === 0) return fetchOne(marketsIndexUrl(rest, q), opts);
  const per = Math.max(1, opts.gamesPerRequest ?? DEFAULT_GAMES_PER_REQUEST);
  const out: HydraMarket[] = [];
  for (let i = 0; i < q.games.length; i += per) {
    const batch = q.games.slice(i, i + per);
    out.push(...(await fetchOne(marketsIndexUrl(rest, q, batch), opts)));
  }
  return out;
}
